import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { formatDate } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import StatusBadge from "@/components/StatusBadge";
import ConfirmDialog from "@/components/ConfirmDialog";
import { F } from "@/pages/Employees";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { NAV, NOT_GRANTABLE } from "@/lib/nav";
import { Checkbox } from "@/components/ui/checkbox";

const empty = { name: "", email: "", role: "viewer", password: "", active: true, employee_id: "", extra_menus: [] };
const MANDOR_MENUS = ["/attendance", "/kasbon-requests", "/reports/mandor-check", "/vehicles"];

const MenuAccess = ({ role, value, onChange }) => {
  if (role === "super_admin") return <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500">Super Admin otomatis bisa membuka semua menu.</div>;
  const toggle = (to) => onChange(value.includes(to) ? value.filter((x) => x !== to) : [...value, to]);
  return (
    <div className="rounded-lg border border-slate-200 max-h-72 overflow-y-auto scroll-thin" data-testid="user-menu-access">
      {NAV.map((g) => {
        const items = g.items.filter((it) => !NOT_GRANTABLE.includes(it.to));
        if (!items.length) return null;
        return (
          <div key={g.title} className="border-b last:border-0 px-3 py-2">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">{g.title}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {items.map((it) => {
                const bawaan = !it.roles?.length || it.roles.includes(role);
                const id = `menu-${it.to.replace(/\//g, "_") || "home"}`;
                return (
                  <label key={it.to} htmlFor={id} className={`flex items-center gap-2 text-sm ${bawaan ? "text-slate-400" : "cursor-pointer"}`}>
                    <Checkbox id={id} data-testid={`user-menu-${it.to === "/" ? "dashboard" : it.to.slice(1).replace(/\//g, "-")}`}
                      checked={bawaan || value.includes(it.to)} disabled={bawaan} onCheckedChange={() => toggle(it.to)} />
                    {it.label}{bawaan && <span className="text-[10px]">(bawaan role)</span>}
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default function Users() {
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [del, setDel] = useState(null);

  const load = () => api.get("/users").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/roles").then((r) => setRoles(r.data)); api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {}); }, []);
  const roleLabel = (k) => roles.find((r) => r.key === k)?.label || k;

  const save = async () => {
    if (!form.name || !form.email) return toast.error("Nama & email wajib diisi");
    try {
      if (editing) await api.put(`/users/${editing}`, form);
      else await api.post("/users", form);
      toast.success("User tersimpan"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/users/${del.id}`); toast.success("User dihapus"); setDel(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const columns = [
    { key: "name", label: "Nama", sortable: true, render: (r) => <span className="font-semibold">{r.name}</span> },
    { key: "email", label: "Email", sortable: true },
    { key: "role", label: "Role", render: (r) => <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{roleLabel(r.role)}</span> },
    { key: "extra_menus", label: "Menu Tambahan", render: (r) => (r.extra_menus || []).length ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-50 text-orange-700" data-testid={`user-extra-${r.id}`}>+{r.extra_menus.length} menu</span> : <span className="text-slate-300">—</span> },
    { key: "active", label: "Status", render: (r) => <StatusBadge status={r.active ? "Aktif" : "Nonaktif"} /> },
    {
      key: "act", label: "Aksi", render: (r) => (
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" data-testid={`user-edit-${r.id}`} onClick={() => { setForm({ name: r.name, email: r.email, role: r.role, password: "", active: r.active, employee_id: r.employee_id || "", extra_menus: r.extra_menus || [] }); setEditing(r.id); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="text-red-600" data-testid={`user-delete-${r.id}`} onClick={() => setDel(r)}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Pengguna & Akses" subtitle="Kelola akun dan hak akses (role)">
        <Button className="bg-blue-800 hover:bg-blue-900" data-testid="user-add-btn" onClick={() => { setForm(empty); setEditing(null); setOpen(true); }}><Plus className="h-4 w-4 mr-1" /> Tambah User</Button>
      </PageHeader>
      <DataTable columns={columns} data={rows} searchKeys={["name", "email"]} testid="user-table" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Tambah"} User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <F label="Nama *"><Input data-testid="user-f-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
            <F label="Email *"><Input type="email" data-testid="user-f-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} /></F>
            <F label="Role">
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-f-role"><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            {(form.role === "site_admin" || form.extra_menus.some((m) => MANDOR_MENUS.includes(m))) && (
              <F label={form.role === "site_admin" ? "Tautkan ke Karyawan (Pimpinan)" : "Tautkan ke Mandor (data tim untuk menu Admin Site)"}>
                <Select value={form.employee_id || "none"} onValueChange={(v) => setForm({ ...form, employee_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="user-f-employee"><SelectValue placeholder="Pilih karyawan pimpinan" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">- Tidak ditautkan -</SelectItem>
                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.nama}{e.is_pimpinan ? " (Pimpinan)" : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
            )}
            <F label={editing ? "Kata Sandi Baru (opsional)" : "Kata Sandi"}><Input type="password" data-testid="user-f-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "Kosongkan jika tidak diubah" : "default: password123"} /></F>
            <F label="Akses Menu Tambahan (di luar role)">
              <MenuAccess role={form.role} value={form.extra_menus} onChange={(v) => setForm({ ...form, extra_menus: v })} />
            </F>
            <F label="Status">
              <Select value={form.active ? "1" : "0"} onValueChange={(v) => setForm({ ...form, active: v === "1" })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="1">Aktif</SelectItem><SelectItem value="0">Nonaktif</SelectItem></SelectContent>
              </Select>
            </F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={save} data-testid="user-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} destructive testid="user-delete-dialog"
        title="Hapus User?" description={`Yakin menghapus ${del?.name}?`} confirmText="Hapus" onConfirm={doDelete} />
    </div>
  );
}
